import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SiteService } from 'src/app/services/site.service';
import {
	CommActivityPointDto,
	CommDashboardService,
	CommDashboardSummaryDto,
	CommEmployerDto,
	Period
} from './comm-dashboard.service';

type EmployerStage = 'build' | 'staged' | 'active';
type CommPreference = 'email' | 'sms' | 'both';

interface EmployerSummary {
	id: string;
	name: string;
	campaignsSent: number;
	campaignsTotal: number;
	dripStartDate: Date | null;
	censusLoaded: number;
	commPreference: CommPreference;
	emailDeliveredPct: number | null;
	smsDeliveredPct: number | null;
	lastCampaign: Date | null;
	nextCampaign: Date | null;
	hasMissingFields: boolean;
	warnings: string[];
	stage: EmployerStage;
}

interface ChartSeries {
	label: string;
	color: string;
	data: number[];
	points?: string;
	areaPoints?: string;
	dots?: { x: number; y: number }[];
}

interface ChartData {
	series: ChartSeries[];
	gridLines: { y: number; label: string }[];
	xLabels: { x: number; label: string }[];
}

@Component({
	selector: 'app-comm-dashboard',
	templateUrl: './comm-dashboard.component.html',
	styleUrls: ['./comm-dashboard.component.scss']
})
export class CommDashboardComponent implements OnInit {

	public period: Period = 'week';
	public sortColumn = 'name';
	public sortDirection: 'asc' | 'desc' = 'asc';

	public loading = true;
	public loadError = false;

	public emailChart: ChartData;
	public smsChart: ChartData;

	public emailStats = {
		requests: 0, delivered: 0, deliveredPct: 0,
		opened: 0, openedPct: 0, clicked: 0, clickedPct: 0,
		bounces: 0, bouncesPct: 0, spamReports: 0, spamPct: 0,
		unsubscribed: 0, unsubscribedPct: 0
	};

	public smsStats = {
		sent: 0, delivered: 0, deliveredPct: 0,
		undelivered: 0, undeliveredPct: 0, failed: 0, failedPct: 0
	};

	public employers: EmployerSummary[] = [];

	private activityPoints: CommActivityPointDto[] = [];

	private readonly weekdayAbbr = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	private readonly monthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	constructor(
		private siteService: SiteService,
		private router: Router,
		private dashboard: CommDashboardService
	) {}

	ngOnInit() {
		this.siteService.pageTitle = 'Communication Dashboard';
		this.siteService.headerButtons = [];
		this.siteService.backButton = null;
		this.loadAll();
	}

	// ── Data loading ──────────────────────────────────────────────────

	reload() {
		this.loadAll();
	}

	private loadAll() {
		this.loading = true;
		this.loadError = false;
		forkJoin({
			employers: this.dashboard.getEmployers(),
			summary: this.dashboard.getSummary(this.period),
			activity: this.dashboard.getActivity(this.period)
		})
			.pipe(finalize(() => (this.loading = false)))
			.subscribe({
				next: ({ employers, summary, activity }) => {
					this.employers = employers.map(e => this.mapEmployer(e));
					this.sortEmployers(this.sortColumn, false);
					this.applySummary(summary);
					this.activityPoints = activity;
					this.buildCharts();
				},
				error: () => (this.loadError = true)
			});
	}

	setPeriod(p: Period) {
		if (this.period === p) {
			return;
		}
		this.period = p;
		forkJoin({
			summary: this.dashboard.getSummary(this.period),
			activity: this.dashboard.getActivity(this.period)
		}).subscribe({
			next: ({ summary, activity }) => {
				this.applySummary(summary);
				this.activityPoints = activity;
				this.buildCharts();
			},
			error: () => (this.loadError = true)
		});
	}

	private applySummary(s: CommDashboardSummaryDto) {
		this.emailStats = {
			requests: s.emailRequests, delivered: s.emailDelivered, deliveredPct: s.emailDeliveredPct,
			opened: s.emailOpened, openedPct: s.emailOpenedPct, clicked: s.emailClicked, clickedPct: s.emailClickedPct,
			bounces: s.emailBounces, bouncesPct: s.emailBouncesPct, spamReports: s.emailSpamReports, spamPct: s.emailSpamPct,
			unsubscribed: s.emailUnsubscribed, unsubscribedPct: s.emailUnsubscribedPct
		};
		this.smsStats = {
			sent: s.smsSent, delivered: s.smsDelivered, deliveredPct: s.smsDeliveredPct,
			undelivered: s.smsUndelivered, undeliveredPct: s.smsUndeliveredPct, failed: s.smsFailed, failedPct: s.smsFailedPct
		};
	}

	private mapEmployer(e: CommEmployerDto): EmployerSummary {
		return {
			id: e.employerID,
			name: e.employerName,
			campaignsSent: e.campaignsSent,
			campaignsTotal: e.campaignsTotal,
			dripStartDate: e.dripStartDate ? new Date(e.dripStartDate) : null,
			censusLoaded: e.censusLoaded,
			commPreference: this.mapCommPref(e.deliveryTypeID),
			emailDeliveredPct: e.emailDeliveredPct,
			smsDeliveredPct: e.smsDeliveredPct,
			lastCampaign: e.lastCampaign ? new Date(e.lastCampaign) : null,
			nextCampaign: e.nextCampaign ? new Date(e.nextCampaign) : null,
			hasMissingFields: e.hasMissingFields,
			warnings: e.hasIssues ? ['Review — open employer for alert details'] : [],
			stage: e.stage
		};
	}

	// deliveryTypeID -> commPreference (see CommDeliveryType enum):
	// 1 Always Email+SMS, 2 SMS-when-no-email (Email+SMS), 3 SMS only, 4 Email only.
	private mapCommPref(deliveryTypeID: number): CommPreference {
		if (deliveryTypeID === 3) return 'sms';
		if (deliveryTypeID === 4) return 'email';
		return 'both';
	}

	// ── Charts ────────────────────────────────────────────────────────

	private buildCharts() {
		const groups = this.period === 'month'
			? this.weeklyBuckets(this.activityPoints)
			: this.activityPoints.map(p => [p]);

		if (groups.length < 2) {
			this.emailChart = this.smsChart = null;
			return;
		}

		const labels = groups.map(g => this.period === 'month'
			? this.monthDayLabel(g[0].activityDate)
			: this.weekdayLabel(g[0].activityDate));

		const sum = (g: CommActivityPointDto[], key: keyof CommActivityPointDto) =>
			g.reduce((acc, p) => acc + (p[key] as number), 0);

		const emailSeries: ChartSeries[] = [
			{ label: 'Sent', color: '#072B31', data: groups.map(g => sum(g, 'emailSent')) },
			{ label: 'Delivered', color: '#58a0ed', data: groups.map(g => sum(g, 'emailDelivered')) },
			{ label: 'Opened', color: '#8ac040', data: groups.map(g => sum(g, 'emailOpened')) },
			{ label: 'Clicked', color: '#FDD26E', data: groups.map(g => sum(g, 'emailClicked')) },
			{ label: 'Bounced', color: '#ea780e', data: groups.map(g => sum(g, 'emailBounced')) },
		];
		const smsSeries: ChartSeries[] = [
			{ label: 'Sent', color: '#072B31', data: groups.map(g => sum(g, 'smsSent')) },
			{ label: 'Delivered', color: '#58a0ed', data: groups.map(g => sum(g, 'smsDelivered')) },
			{ label: 'Undelivered', color: '#ea780e', data: groups.map(g => sum(g, 'smsUndelivered')) },
			{ label: 'Failed', color: '#B83B2F', data: groups.map(g => sum(g, 'smsFailed')) },
		];

		this.emailChart = this.buildChart(emailSeries, labels);
		this.smsChart = this.buildChart(smsSeries, labels);
	}

	private weeklyBuckets(points: CommActivityPointDto[]): CommActivityPointDto[][] {
		const buckets: CommActivityPointDto[][] = [];
		for (let i = 0; i < points.length; i += 7) {
			buckets.push(points.slice(i, i + 7));
		}
		return buckets;
	}

	private weekdayLabel(iso: string): string {
		return this.weekdayAbbr[new Date(iso).getDay()];
	}

	private monthDayLabel(iso: string): string {
		const d = new Date(iso);
		return `${this.monthAbbr[d.getMonth()]} ${d.getDate()}`;
	}

	buildChart(series: ChartSeries[], labels: string[]): ChartData {
		const allVals = series.flatMap(s => s.data);
		const maxVal = Math.max(...allVals, 1);
		const niceMax = this.getNiceMax(maxVal);
		const n = labels.length;

		const computedSeries: ChartSeries[] = series.map(s => ({
			...s,
			points: s.data.map((v, i) => `${this.cx(i, n).toFixed(1)},${this.cy(v, niceMax).toFixed(1)}`).join(' '),
			areaPoints: [
				...s.data.map((v, i) => `${this.cx(i, n).toFixed(1)},${this.cy(v, niceMax).toFixed(1)}`),
				`${this.cx(n - 1, n).toFixed(1)},165`,
				`${this.cx(0, n).toFixed(1)},165`
			].join(' '),
			dots: s.data.map((v, i) => ({ x: this.cx(i, n), y: this.cy(v, niceMax) }))
		}));

		return {
			series: computedSeries,
			gridLines: this.getGridLines(niceMax),
			xLabels: labels.map((label, i) => ({ label, x: this.cx(i, n) }))
		};
	}

	private cx(i: number, n: number): number {
		return 55 + i * (415 / (n - 1));
	}

	private cy(val: number, max: number): number {
		return 165 - (val / max) * 155;
	}

	private getNiceMax(maxVal: number): number {
		if (maxVal <= 0) return 10;
		const exp = Math.floor(Math.log10(maxVal));
		const factor = Math.pow(10, exp);
		const norm = maxVal / factor;
		if (norm <= 1) return factor;
		if (norm <= 2) return 2 * factor;
		if (norm <= 5) return 5 * factor;
		return 10 * factor;
	}

	private getGridLines(niceMax: number): { y: number; label: string }[] {
		const step = niceMax / 4;
		return [0, 1, 2, 3, 4].map(i => {
			const val = i * step;
			const y = parseFloat(this.cy(val, niceMax).toFixed(1));
			const label = val >= 1000 ? `${(val / 1000).toFixed(val >= 10000 ? 0 : 1)}k` : `${Math.round(val)}`;
			return { y, label };
		});
	}

	// ── Grid ──────────────────────────────────────────────────────────

	sortEmployers(column: string, toggle: boolean = true) {
		if (toggle) {
			this.sortDirection = this.sortColumn === column
				? (this.sortDirection === 'asc' ? 'desc' : 'asc')
				: 'asc';
		}
		this.sortColumn = column;

		const stageOrder: Record<EmployerStage, number> = { build: 0, staged: 1, active: 2 };

		this.employers.sort((a, b) => {
			let va: any;
			let vb: any;

			if (column === 'stage') {
				va = stageOrder[this.getStage(a)];
				vb = stageOrder[this.getStage(b)];
			} else if (column === 'campaigns') {
				va = a.campaignsTotal > 0 ? a.campaignsSent / a.campaignsTotal : -1;
				vb = b.campaignsTotal > 0 ? b.campaignsSent / b.campaignsTotal : -1;
			} else {
				va = (a as any)[column];
				vb = (b as any)[column];
			}

			if (va === null || va === undefined) va = this.sortDirection === 'asc' ? Infinity : -Infinity;
			if (vb === null || vb === undefined) vb = this.sortDirection === 'asc' ? Infinity : -Infinity;

			if (va instanceof Date && vb instanceof Date) {
				va = va.getTime(); vb = vb.getTime();
			} else if (typeof va === 'string') {
				va = va.toLowerCase(); vb = (vb as string).toLowerCase();
			}
			if (va < vb) return this.sortDirection === 'asc' ? -1 : 1;
			if (va > vb) return this.sortDirection === 'asc' ? 1 : -1;
			return 0;
		});
	}

	// Stage is computed in upCommDashboardEmployers (single source of truth); the client only displays it.
	getStage(emp: EmployerSummary): EmployerStage {
		return emp.stage;
	}

	getStageLabel(stage: EmployerStage): string {
		return stage.charAt(0).toUpperCase() + stage.slice(1);
	}

	getCommPrefIcon(pref: CommPreference): string {
		if (pref === 'email') return 'mail';
		if (pref === 'sms') return 'sms';
		return 'forum';
	}

	getCommPrefLabel(pref: CommPreference): string {
		if (pref === 'email') return 'Email';
		if (pref === 'sms') return 'SMS';
		return 'Email + SMS';
	}

	getSortIcon(col: string): string {
		if (this.sortColumn !== col) return 'sort';
		return this.sortDirection === 'asc' ? 'sort-up' : 'sort-down';
	}

	getStatus(emp: EmployerSummary): 'ok' | 'warning' {
		return emp.warnings.length === 0 ? 'ok' : 'warning';
	}

	openEmployer(emp: EmployerSummary) {
		this.router.navigate(['/admin', 'campaigns', 'comm-dashboard', emp.id]);
	}
}
